/**
 * First 100 Days on Mandate's record (roadmap S-16, spec
 * docs/9-exploration/app-layer/SPEC-individual-apps.md §2.4). No store of its
 * own; each part of the journey is kept on an object that already exists:
 *
 *   - day counter, phases  → Mandate's day count (getDayNumber) from its start date
 *   - stakeholder map      → Mandate's stakeholders
 *   - listening tour       → a dated note on the stakeholder, and an item for
 *                            each insight, tension or opportunity raised,
 *                            tagged "heard in the listening tour"
 *   - priority discovery   → an item per priority; its disposition is a tag
 *                            on the item ("disposition: fix"), not a list
 *   - Day 101              → a personal Stack Ranking of the new, accelerate
 *                            and fix items, recorded as a Mandate decision
 *                            (mirrored to the shared decisions record)
 */

import type { Item } from '@/types/prioritization/lifecycle';
import type { MandateDecision } from '@/types/mandate';
import { PHASES, PRIORITY_DISCOVERY_FRAMEWORK, type Phase } from './framework';

export const FIRST_100_DAYS_APP_ID = 'first-100-days';

/** Every item the journey makes carries this tag. */
export const JOURNEY_TAG = 'first 100 days';
export const LISTENING_TOUR_TAG = 'heard in the listening tour';

// ─── Day and phase ──────────────────────────────────────────────────────────

/** The phase a day falls in; after day 100 the last phase holds. */
export function phaseForDay(day: number): Phase {
  return PHASES.find((p) => day >= p.days[0] && day <= p.days[1]) ?? PHASES[PHASES.length - 1];
}

// ─── Listening tour ─────────────────────────────────────────────────────────

export type HeardKind = 'insight' | 'tension' | 'opportunity';
export const HEARD_KINDS: HeardKind[] = ['insight', 'tension', 'opportunity'];

const NOTE_PREFIX = 'Listening tour, ';

/** The note one conversation adds to the person's stakeholder notes. */
export function listeningNote(on: Date, heard: string): string {
  const day = on.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${NOTE_PREFIX}${day}: ${heard.trim()}`;
}

/** The stakeholder's notes with one more conversation at the end. */
export function appendNote(existing: string | null | undefined, note: string): string {
  const before = (existing ?? '').trim();
  return before ? `${before}\n\n${note}` : note;
}

/** The listening-tour conversations in a stakeholder's notes, oldest first. */
export function conversationsIn(notes: string | null | undefined): Array<{ day: string; heard: string }> {
  return (notes ?? '')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.startsWith(NOTE_PREFIX))
    .map((block) => {
      const rest = block.slice(NOTE_PREFIX.length);
      const colon = rest.indexOf(': ');
      return colon < 0 ? { day: '', heard: rest } : { day: rest.slice(0, colon), heard: rest.slice(colon + 2) };
    });
}

/** Tags for an item raised in the listening tour. */
export function heardTags(kind: HeardKind): string[] {
  return [JOURNEY_TAG, LISTENING_TOUR_TAG, kind];
}

// ─── Priority discovery ─────────────────────────────────────────────────────

export type Disposition = 'inherit' | 'accelerate' | 'fix' | 'stop' | 'new';
export const DISPOSITIONS = PRIORITY_DISCOVERY_FRAMEWORK.statuses as ReadonlyArray<{
  id: Disposition;
  label: string;
  description: string;
}>;

const DISPOSITION_PREFIX = 'disposition: ';

export function dispositionOf(item: Pick<Item, 'tags'>): Disposition | null {
  const tag = (item.tags ?? []).find((t) => t.startsWith(DISPOSITION_PREFIX));
  const value = tag?.slice(DISPOSITION_PREFIX.length);
  return DISPOSITIONS.some((d) => d.id === value) ? (value as Disposition) : null;
}

/** The item's tags with its disposition set (one disposition per item). */
export function withDisposition(tags: readonly string[], disposition: Disposition): string[] {
  const rest = tags.filter((t) => !t.startsWith(DISPOSITION_PREFIX));
  const withJourney = rest.includes(JOURNEY_TAG) ? rest : [...rest, JOURNEY_TAG];
  return [...withJourney, `${DISPOSITION_PREFIX}${disposition}`];
}

/** The priorities sorted in discovery, in the order they were added. */
export function discoveredPriorities(items: readonly Item[]): Item[] {
  return items.filter((item) => (item.exit_status ?? 'active') === 'active' && dispositionOf(item) !== null);
}

/** Day 101 ranks what the leader will drive: new, accelerate and fix. */
export const DAY_101_DISPOSITIONS: readonly Disposition[] = ['new', 'accelerate', 'fix'];

export function day101Candidates(items: readonly Item[]): Item[] {
  return discoveredPriorities(items).filter((item) => DAY_101_DISPOSITIONS.includes(dispositionOf(item)!));
}

// ─── Day 101 ────────────────────────────────────────────────────────────────

export const DAY_101_DECISION_TITLE = 'Day 101: my first priorities';

/** The decision that records the Day 101 top three to five. */
export function day101Decision(top: readonly Item[], sessionId: string): {
  title: string;
  description: string;
  rationale: string;
  signal: string;
} {
  return {
    title: DAY_101_DECISION_TITLE,
    description: top.map((item, index) => `${index + 1}. ${item.title}`).join('\n'),
    rationale:
      'Ranked at Day 101 from the priorities sorted in discovery (new, accelerate and fix), after the listening tour.',
    signal: `Personal ranking session ${sessionId}`,
  };
}

/** The latest Day 101 decision on the mandate, if one was recorded. */
export function latestDay101Decision(decisions: readonly MandateDecision[]): MandateDecision | undefined {
  return [...decisions]
    .filter((d) => d.title === DAY_101_DECISION_TITLE)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}
